import React, { FC, useState, useMemo, ChangeEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './App.css'; // Assuming event card styles will be in App.css or a new CSS file
import './components/LoadingDisplay.css';
import EventCalendarView from './EventCalendarView'; // Import the new calendar view
import EventMapView from './EventMapView'; // Import the new map view
import ErrorDisplay from './components/ErrorDisplay';
import api from './api';
import { formatEventTime } from './shared/date.utils';

interface Organization {
  id: string;
  name: string;
}

// Export EventItem to be used by other components like EventCalendarView
export interface EventItem {
  id: string;
  name: string; // Pre-translated
  startsAt: string; // Unix timestamp as bigint string
  endsAt: string; // Unix timestamp as bigint string
  summary: string; // Pre-translated
  place: string; // Pre-translated
  description: string; // Pre-translated
  imageUrl: string;
  imageAlt: string; // Pre-translated alt text
  organization: Organization; // The organization that created the event
  coordinates?: { lat: number; lng: number }; // Optional coordinates for map
}

const itemsPerPageOptions = [10, 25, 50]; 

type EventViewType = 'list' | 'grid' | 'calendar' | 'map';

const EventsPage: FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth(); // Get user from auth context
  const isInitialMount = useRef(true); // Ref to track initial mount

  const [events, setEvents] = useState<EventItem[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [totalEvents, setTotalEvents] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('upcoming'); // 'all', 'upcoming', 'past'
  const [currentView, setCurrentView] = useState<EventViewType>('list');
  const [currentItemsPerPage, setCurrentItemsPerPage] = useState<number>(itemsPerPageOptions[0]); // Default to the first option

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const params = new URLSearchParams();

        params.append('pageSize', '1000');
        params.append('include', 'id');
        params.append('include', 'name');

        const orgs = await api(`/organizations?${params.toString()}`);
        setOrganizations(orgs.organizations);
      } catch (e) {
        console.error("failed to fetch organizations for filter");
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (selectedOrganization !== 'all') {
          const org = organizations.find(o => o.name === selectedOrganization);
          if (org) {
            params.append('organizationId', org.id);
          }
        }
        if (selectedDateFilter !== 'all') {
          params.append('dateFilter', selectedDateFilter);
        }

        if (currentView === 'list' || currentView === 'grid') {
          params.append('page', String(currentPage));
          params.append('pageSize', String(currentItemsPerPage));
        } else {
          params.append('pageSize', '1000'); // For calendar/map view, fetch all matching events
        }

        const response = await api(`/events?${params.toString()}`);
        console.log('Events API response:', response); // Debug log

        if (response && response.data && Array.isArray(response.data)) {
          // Paginated response: { data: [], total: N }
          setEvents(response.data);
          // Ensure total is a valid number
          const total = typeof response.total === 'number' ? response.total : response.data.length;
          setTotalEvents(total);
          console.log('Set totalEvents to:', total); // Debug log
        } else if (Array.isArray(response)) {
          // Direct array response (non-paginated)
          setEvents(response);
          setTotalEvents(response.length);
          console.log('Set totalEvents to array length:', response.length); // Debug log
        } else {
          // Fallback for undefined or unexpected responses
          setEvents([]);
          setTotalEvents(0);
          console.log('Fallback: set totalEvents to 0'); // Debug log
        }
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setError('Failed to load events.');
        setEvents([]);
        setTotalEvents(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, [currentPage, currentItemsPerPage, selectedOrganization, selectedDateFilter, currentView, organizations]);

  // Scroll to top when page or items per page changes, but not on initial load
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      if (!isLoading && (currentView === 'list' || currentView === 'grid')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [currentPage, currentItemsPerPage, isLoading, currentView]); // Dependencies for the scroll effect

  const uniqueHostingOrganizations = useMemo(() => {
    const allOrgs = new Map<string, string>();
    organizations.forEach(org => {
      allOrgs.set(org.id, org.name);
    });
    return ['all', ...Array.from(allOrgs.values()).sort()];
  }, [organizations]);

  // Ensure totalPages is always a valid number
  const totalPages = Math.max(1, Math.ceil(totalEvents / currentItemsPerPage));

  const handleOrganizationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedOrganization(event.target.value);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedDateFilter(event.target.value);
    setCurrentPage(1);
  };

  const handleViewChange = (view: EventViewType) => {
    setCurrentView(view);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setCurrentItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  
  // This function will be used when filters are added to reset page
  // const resetPageAndApplyFilters = () => { 
  //   setCurrentPage(1);
  //   // ... apply filter logic ...
  // };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="loading-display">
          <div className="loading-spinner"></div>
          <p>Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <ErrorDisplay 
          error={error}
          title="Failed to load events"
          onRetry={() => {
            setError(null);
            // This will trigger the useEffect to refetch events
            setCurrentPage(1);
          }}
        />
      </div>
    );
  }

  const renderEventContent = () => {
    if (currentView !== 'calendar' && currentView !== 'map' && events.length === 0) {
        return <p>{t('no_events_match_filters')}</p>;
    }
    if ((currentView === 'list' || currentView === 'grid') && events.length === 0 && totalEvents > 0) {
      return <p>{t('no_events_match_filters')}</p>; // Or a more specific pagination error
    }
    if (events.length === 0 && (currentView === 'calendar' || currentView === 'map')){
        // For calendar/map, if eventsToFilter is empty, show a message before attempting to render the component
        return <p>{t('no_events_available')}</p>;
    }

    switch (currentView) {
      case 'list':
        return (
          <div className="event-list list-view">
            {events.map(event => (
              <div key={event.id} className="event-card-list-item-wrapper">
                <Link to={`/events/${event.id}`} className="event-card-list-item-link">
                  <div className="event-card-list-item">
                    <img src={event.imageUrl} alt={event.imageAlt} className="event-image" crossOrigin="anonymous"/>
                    <div className="event-details">
                      <h3 className="event-name">{event.name}</h3>
                      <p className="event-time">{formatEventTime(event.startsAt, i18n.language)} - {formatEventTime(event.endsAt, i18n.language)}</p>
                      {event.organization && <p className="event-org">{t('event_hosted_by_label')} {event.organization.name}</p>}
                      <p className="event-place">{event.place}</p>
                      <p className="event-summary">{event.summary}</p>
                    </div>
                  </div>
                </Link>
                {user && (
                  <div className="event-actions">
                    <Link to={`/events/${event.id}/edit`} className="button-secondary event-edit-button">
                      Edit
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      case 'grid':
        return (
          <div className="event-list grid-view">
            {events.map(event => (
              <div key={event.id} className="event-card-grid-item-wrapper">
                <Link to={`/events/${event.id}`} className="event-card-grid-item-link">
                  <div className="event-card-grid-item">
                    <img src={event.imageUrl} alt={event.imageAlt} className="event-image" crossOrigin="anonymous" />
                    <div className="event-details">
                      <h3 className="event-name">{event.name}</h3>
                      <p className="event-time">{formatEventTime(event.startsAt, i18n.language)} - {formatEventTime(event.endsAt, i18n.language)}</p>
                      {event.organization && <p className="event-org">{t('event_hosted_by_label')} {event.organization.name}</p>}
                      <p className="event-place">{event.place}</p>
                      <p className="event-summary">{event.summary}</p>
                    </div>
                  </div>
                </Link>
                {user && (
                  <div className="event-actions">
                    <Link to={`/events/${event.id}/edit`} className="button-secondary event-edit-button">
                      Edit
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      case 'calendar':
        return <EventCalendarView events={events} />;
      default:
        return null;
    }
  };

  return (
    <div className="events-page-container">
      <h1 className="page-title">{t('events_page_title')}</h1>
      <p className="page-description">{t('events_page_description')}</p>
      
      {user && (
        <div className="page-actions">
          <Link to="/events/new" className="button-primary">Create Event</Link>
        </div>
      )}
      
      <div className="project-controls view-controls-container">
        <div className="filter-controls-group">
          <div className="filter-group">
            <label htmlFor="org-filter" className="filter-label">{t('filter_by_organization_label')}</label>
            <select id="org-filter" value={selectedOrganization} onChange={handleOrganizationChange} className="filter-select">
              {uniqueHostingOrganizations.map(orgName => (
                <option key={orgName} value={orgName}>
                  {orgName === 'all' ? t('all_organizations_label') : orgName}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="date-filter" className="filter-label">{t('filter_by_date_label')}</label>
            <select id="date-filter" value={selectedDateFilter} onChange={handleDateFilterChange} className="filter-select">
              <option value="all">{t('date_filter_all')}</option>
              <option value="upcoming">{t('date_filter_upcoming')}</option>
              <option value="past">{t('date_filter_past')}</option>
            </select>
          </div>
        </div>
        <div className="view-switcher">
          {(['list', 'grid', 'calendar', 'map'] as EventViewType[]).map(view => (
            <button 
              key={view} 
              onClick={() => handleViewChange(view)} 
              className={`view-button ${currentView === view ? 'active' : ''}`}>
              {t(`view_button_${view}` as any)}
            </button>
          ))}
        </div>
      </div>

      {renderEventContent()}

      {/* Show pagination for list and grid views */}
      {(currentView === 'list' || currentView === 'grid') && (
        <div className="pagination-controls">
          <button onClick={goToPreviousPage} disabled={currentPage === 1 || totalPages <= 1} className="pagination-button">
            {t('pagination_previous')}
          </button>
          <span className="pagination-info">
            {t('pagination_page_info', { 
              currentPage: Math.max(1, currentPage), 
              totalPages: Math.max(1, totalPages) 
            })}
          </span>
          <button onClick={goToNextPage} disabled={currentPage >= totalPages || totalPages <= 1} className="pagination-button">
            {t('pagination_next')}
          </button>
          <div className="filter-group items-per-page-filter" style={{ marginLeft: '20px' }}>
            <label htmlFor="items-per-page-select" className="filter-label">{t('items_per_page_label')}</label>
            <select
              id="items-per-page-select"
              value={currentItemsPerPage}
              onChange={handleItemsPerPageChange}
              className="filter-select"
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventsPage;
 